import type { RestaurantApplicationFormData } from '@services/complianceService';
import {
  validateEmailAddress,
  validateFormDate,
  validateIban,
  validateMersisOrTradeRegistry,
  validateRequiredText,
  validateTaxOffice,
  validateTurkishLandlinePhone,
  validateTurkishMobilePhone,
  validateVknOrTckn,
} from './validation';

export type ApplicationFormFieldKey = keyof RestaurantApplicationFormData;

export interface ApplicationFormFieldConfig {
  key: ApplicationFormFieldKey;
  label: string;
  required: boolean;
  placeholder?: string;
  helperText?: string;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'decimal';
  fullWidth?: boolean;
}

export const APPLICATION_FORM_FIELDS: ApplicationFormFieldConfig[] = [
  {
    key: 'legalName',
    label: 'Yasal Ticari Unvanı / Firma Adı',
    required: true,
    placeholder: 'Örn: Anatolia Gıda Ltd. Şti.',
    helperText: 'Ticaret sicilinde kayıtlı resmi unvan',
    fullWidth: false,
  },
  {
    key: 'tradeName',
    label: 'İşletme / Tabela Adı',
    required: true,
    placeholder: 'Örn: Anatolia Pizza',
    helperText: 'Müşterilerin gördüğü restoran adı',
  },
  {
    key: 'taxOffice',
    label: 'Vergi Dairesi',
    required: true,
    placeholder: 'Örn: Seyhan',
    helperText: 'Bağlı olduğunuz vergi dairesi adı',
  },
  {
    key: 'taxNumber',
    label: 'Vergi Kimlik Numarası (VKN / TCKN)',
    required: true,
    placeholder: '10 veya 11 haneli',
    helperText: 'Şirketler için 10 haneli VKN, şahıs işletmeleri için 11 haneli TCKN',
    inputMode: 'numeric',
  },
  {
    key: 'mersisNumber',
    label: 'Mersis Numarası / Ticaret Sicil No',
    required: true,
    placeholder: '16 haneli MERSİS veya sicil no',
    helperText: 'MERSİS tamamen rakamsa 16 hane; aksi halde ticaret sicil numaranızı girin',
  },
  {
    key: 'address',
    label: 'İş Yeri Resmi Adresi',
    required: true,
    placeholder: 'Mahalle, cadde, bina no, ilçe/il',
    helperText: 'Vergi levhasındaki adres ile uyumlu olmalıdır',
    fullWidth: true,
  },
  {
    key: 'phone',
    label: 'Sabit Telefon Numarası',
    required: true,
    placeholder: '322 123 45 67',
    helperText: 'Alan kodu ile birlikte 10 haneli sabit hat',
    inputMode: 'tel',
  },
  {
    key: 'email',
    label: 'İş E-posta',
    required: true,
    placeholder: 'ornek@sirket.com',
    inputMode: 'email',
  },
  {
    key: 'authorizedPersonName',
    label: 'Firma Yetkilisi / Ortak Adı Soyadı',
    required: true,
    placeholder: 'Ad Soyad',
  },
  {
    key: 'authorizedPersonPhone',
    label: 'Yetkili Cep Telefonu Numarası',
    required: true,
    placeholder: '5XX XXX XX XX',
    helperText: 'Türkiye mobil numarası (5 ile başlar)',
    inputMode: 'tel',
  },
  {
    key: 'bankAccountHolder',
    label: 'Banka Hesap Sahibi Unvanı / Adı Soyadı',
    required: true,
    placeholder: 'Hesap sahibi adı veya unvanı',
    helperText: 'IBAN ile birebir aynı olmalıdır',
  },
  {
    key: 'iban',
    label: 'Hakediş IBAN Numarası',
    required: true,
    placeholder: 'TR00 0000 0000 0000 0000 0000 00',
    helperText: 'TR ile başlayan 26 karakter',
  },
  {
    key: 'formDate',
    label: 'Form Doldurma Tarihi',
    required: true,
    placeholder: 'YYYY-MM-DD',
    helperText: 'Bugünün veya geçmiş bir tarih',
  },
];

export type ApplicationFormFieldErrors = Partial<Record<ApplicationFormFieldKey, string>>;

export function validateApplicationFormField(
  key: ApplicationFormFieldKey,
  value: string,
): { valid: boolean; message?: string } {
  switch (key) {
    case 'legalName':
      return validateRequiredText(value, 'Yasal ticari unvan', 2, 255);
    case 'tradeName':
      return validateRequiredText(value, 'İşletme adı', 2, 255);
    case 'taxOffice':
      return validateTaxOffice(value, true);
    case 'taxNumber':
      return validateVknOrTckn(value, true);
    case 'mersisNumber':
      return validateMersisOrTradeRegistry(value, true);
    case 'address':
      return validateRequiredText(value, 'İş yeri adresi', 10, 500);
    case 'phone':
      return validateTurkishLandlinePhone(value, true);
    case 'email':
      return validateEmailAddress(value, true);
    case 'authorizedPersonName':
      return validateRequiredText(value, 'Yetkili adı soyadı', 3, 120);
    case 'authorizedPersonPhone': {
      if (!value.trim()) {
        return { valid: false, message: 'Yetkili cep telefonu zorunludur.' };
      }
      return validateTurkishMobilePhone(value);
    }
    case 'bankAccountHolder':
      return validateRequiredText(value, 'Banka hesap sahibi', 2, 255);
    case 'iban':
      return validateIban(value, true);
    case 'formDate':
      return validateFormDate(value);
    default:
      return { valid: true };
  }
}

export function validateApplicationForm(
  formData: RestaurantApplicationFormData,
): ApplicationFormFieldErrors {
  const errors: ApplicationFormFieldErrors = {};

  for (const field of APPLICATION_FORM_FIELDS) {
    const result = validateApplicationFormField(field.key, formData[field.key] ?? '');
    if (!result.valid && result.message) {
      errors[field.key] = result.message;
    }
  }

  return errors;
}

export function isApplicationFormValid(formData: RestaurantApplicationFormData): boolean {
  return Object.keys(validateApplicationForm(formData)).length === 0;
}

export function mergeRestaurantPrefill(
  current: RestaurantApplicationFormData,
  prefill: RestaurantApplicationFormData,
  overwrite = false,
): RestaurantApplicationFormData {
  const merged = { ...current };
  for (const field of APPLICATION_FORM_FIELDS) {
    const prefillValue = prefill[field.key]?.trim();
    if (!prefillValue) continue;
    if (overwrite || !merged[field.key]?.trim()) {
      merged[field.key] = prefillValue;
    }
  }
  return merged;
}
